@file:NoLiveLiterals

package com.example.borshi.web.account

import androidx.compose.runtime.*
import com.example.borshi.web.auth.AccountSectionItem
import com.example.borshi.web.auth.AuthState
import com.example.borshi.web.auth.AuthViewModel
import com.example.borshi.web.auth.FirebaseTestAccounts
import com.example.borshi.web.auth.OtpState
import com.example.borshi.web.auth.SignInState
import com.example.borshi.web.auth.SignUpState
import com.example.borshi.web.auth.UserProfile
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.orders.OrderHistoryScreen
import com.example.borshi.web.referral.ReferralScreen
import com.example.borshi.web.wallet.WalletSection
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import androidx.compose.runtime.rememberCoroutineScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// Color constants — aligned with the global premium design system
const val BG_LIGHT      = "#F5F9F8"   // consistent with other screens
const val TEXT_DARK     = "#0D2B2E"   // Deep Ocean
const val PRIMARY_COLOR = "#0D2B2E"   // Deep Ocean (was crimson — now premium ink)
const val TEAL_ACC      = "#26A69A"   // Teal accent — used for active/hover states
const val BORDER_COLOR  = "#E0EDEB"   // Teal-tinted border
const val TEXT_GRAY     = "#7A9598"   // Teal-tinted secondary text
const val PRIMARY_LIGHT = "#C4DBD8"   // Soft teal for disabled states

private const val SHOW_TEST_ACCOUNTS = false

@Composable
private fun InjectAccountStyles() {
    DisposableEffect(Unit) {
        if (kotlinx.browser.document.getElementById("acc-styles") != null) return@DisposableEffect onDispose {}
        val s = kotlinx.browser.document.createElement("style")
        s.id = "acc-styles"
        s.textContent = """
.acc-root{max-width:860px;margin:0 auto;padding:24px 16px 80px;min-height:calc(100vh - 64px)}
.acc-loading{display:flex;flex-direction:column;gap:16px;padding:40px 0}
.acc-skel{border-radius:16px;background:linear-gradient(90deg,#E4EFEE 25%,#F2F9F8 50%,#E4EFEE 75%);background-size:700px 100%;animation:accShimmer 1.5s ease-in-out infinite}
@keyframes accShimmer{0%{background-position:-700px 0}100%{background-position:700px 0}}
.acc-header-card{background:white;border-radius:20px;border:1.5px solid #E0EDEB;overflow:hidden;box-shadow:0 2px 16px rgba(13,43,46,0.06);margin-bottom:24px}
.acc-header-top{display:flex;justify-content:space-between;align-items:center;padding:20px 28px;border-bottom:1px solid #F0F5F4}
.acc-header-title{font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#0D2B2E;letter-spacing:-0.03em}
.acc-signout{font-family:'DM Sans',sans-serif;background:none;border:1.5px solid #C4DBD8;padding:8px 18px;border-radius:10px;font-size:13px;font-weight:600;color:#3D5A5E;cursor:pointer;transition:all 0.18s ease}
.acc-signout:hover{border-color:#0D2B2E;background:#0D2B2E;color:white}
.acc-profile{display:flex;align-items:center;gap:18px;padding:20px 28px;background:#F5F9F8}
.acc-avatar{width:64px;height:64px;border-radius:18px;background:#0D2B2E;display:flex;align-items:center;justify-content:center;font-size:26px;flex-shrink:0}
.acc-name{font-family:'DM Sans',sans-serif;font-size:17px;font-weight:700;color:#0D2B2E}
.acc-sub{font-family:'DM Sans',sans-serif;font-size:13px;color:#7A9598;margin-top:2px}
.acc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:16px}
.acc-back{display:inline-flex;align-items:center;gap:6px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#7A9598;cursor:pointer;margin-bottom:20px;padding:6px 10px;border-radius:8px;transition:all 0.15s ease}
.acc-back:hover{color:#0D2B2E;background:#F0FAFA}
.acc-auth-card{background:white;border-radius:24px;border:1.5px solid #E0EDEB;box-shadow:0 4px 32px rgba(13,43,46,0.08);max-width:480px;margin:32px auto;padding:40px 36px}
.acc-auth-brand{text-align:center;margin-bottom:32px}
.acc-auth-logo{width:64px;height:64px;border-radius:18px;background:#0D2B2E;display:flex;align-items:center;justify-content:center;font-size:28px;margin:0 auto 16px}
.acc-auth-title{font-family:'Syne',sans-serif;font-size:24px;font-weight:800;color:#0D2B2E;letter-spacing:-0.035em}
.acc-auth-sub{font-family:'DM Sans',sans-serif;font-size:13.5px;color:#7A9598;margin-top:6px}
.acc-field{margin-bottom:16px}
.acc-label{display:block;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;color:#3D5A5E;letter-spacing:0.04em;text-transform:uppercase;margin-bottom:6px}
.acc-input{width:100%;padding:12px 14px;font-size:15px;font-family:'DM Sans',sans-serif;border:1.5px solid #E0EDEB;border-radius:12px;outline:none;background:#F5F9F8;color:#0D2B2E;transition:border-color .18s ease,box-shadow .18s ease}
.acc-input:focus{border-color:#26A69A;box-shadow:0 0 0 3px rgba(38,166,154,0.12);background:white}
.acc-btn{width:100%;padding:14px;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:700;border:none;border-radius:12px;cursor:pointer;transition:all 0.18s ease}
.acc-btn-primary{background:#0D2B2E;color:white}
.acc-btn-primary:hover{background:#1A4A4E;transform:translateY(-1px);box-shadow:0 4px 14px rgba(13,43,46,0.22)}
.acc-btn-primary:disabled{background:#C4DBD8;cursor:not-allowed;transform:none;box-shadow:none}
.acc-btn-outline{background:white;border:1.5px solid #E0EDEB;color:#3D5A5E}
.acc-btn-outline:hover{border-color:#26A69A;color:#0D2B2E;background:#F0FAFA}
.acc-divider{display:flex;align-items:center;gap:12px;margin:20px 0}
.acc-divider-line{flex:1;height:1px;background:#F0F5F4}
.acc-divider-text{font-family:'DM Sans',sans-serif;font-size:11px;font-weight:700;color:#7A9598;letter-spacing:0.1em;text-transform:uppercase}
.acc-switch{text-align:center;margin-top:20px;font-family:'DM Sans',sans-serif;font-size:13px;color:#7A9598}
.acc-switch-link{color:#26A69A;font-weight:700;cursor:pointer;background:none;border:none;font-size:13px}
.acc-msg-ok{font-family:'DM Sans',sans-serif;font-size:13px;color:#2E7D32;margin-top:8px}
.acc-msg-err{font-family:'DM Sans',sans-serif;font-size:13px;color:#C62828;margin-top:8px}
.acc-prefix{width:56px;padding:12px 10px;font-size:15px;font-family:'DM Sans',sans-serif;border:1.5px solid #E0EDEB;border-radius:12px;text-align:center;font-weight:700;background:#F5F9F8;color:#0D2B2E}
@media(max-width:600px){.acc-auth-card{padding:28px 20px;margin:16px auto}.acc-grid{grid-template-columns:repeat(auto-fill,minmax(150px,1fr))}}
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(s)
        onDispose {}
    }
}

@Composable
fun AccountScreen() {
    var authState by remember { mutableStateOf<AuthState>(AuthState.Loading) }
    val authViewModel = remember { AuthViewModel() }

    InjectAccountStyles()

    DisposableEffect(Unit) {
        FirebaseService.init()
        val unsubscribe = FirebaseService.onAuthStateChanged { user ->
            authState = if (user != null) {
                AuthState.Authenticated(user)
            } else {
                AuthState.Unauthenticated
            }
        }
        onDispose {
            unsubscribe()
            authViewModel.clear()
        }
    }

    Div({ classes("acc-root") }) {
        when (authState) {
            is AuthState.Loading -> {
                Div({ classes("acc-loading") }) {
                    Div({ classes("acc-skel"); style { height(80.px) } }) {}
                    Div({ classes("acc-skel"); style { height(220.px) } }) {}
                    Div({ style { display(DisplayStyle.Grid); property("grid-template-columns","repeat(auto-fill,minmax(180px,1fr))"); gap(16.px) } }) {
                        repeat(4) { Div({ classes("acc-skel"); style { height(120.px) } }) {} }
                    }
                }
            }
            is AuthState.Authenticated -> {
                var userProfile by remember { mutableStateOf<UserProfile?>(null) }
                var profileRefreshTrigger by remember { mutableStateOf(0) }
                LaunchedEffect(profileRefreshTrigger) {
                    userProfile = com.example.borshi.web.firebase.FirebaseService.getUserProfile()
                }
                val profile = userProfile
                if (profile == null) {
                    Div({ classes("acc-loading") }) {
                        Div({ classes("acc-skel"); style { height(80.px) } }) {}
                    }
                } else if (!isProfileComplete(profile)) {
                    CompleteProfileSection(
                        currentProfile = profile,
                        onComplete = { profileRefreshTrigger++ }
                    )
                } else {
                    AccountDashboard(profile) {
                        authViewModel.signOut()
                    }
                }
            }
            is AuthState.Unauthenticated -> {
                var showSignUp by remember { mutableStateOf(false) }
                if (showSignUp) {
                    SignUpSection(authViewModel) { showSignUp = false }
                } else {
                    LoginSection(authViewModel) { showSignUp = true }
                }
            }
        }
    }
}

private fun isProfileComplete(profile: UserProfile): Boolean {
    val name = profile.name.orEmpty().trim()
    val phone = profile.phoneNumber.orEmpty().replace(Regex("[^0-9]"), "")
    return name.isNotBlank() && phone.length >= 10 && profile.hasValidAddress
}

@Composable
private fun CompleteProfileSection(currentProfile: UserProfile, onComplete: () -> Unit) {
    var fullName by remember { mutableStateOf(currentProfile.name.orEmpty().trim()) }
    var phone by remember { mutableStateOf(currentProfile.phoneNumber.orEmpty().replace(Regex("[^0-9]"), "")) }
    var addressLine1 by remember { mutableStateOf("") }
    var addressLine2 by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var state by remember { mutableStateOf("") }
    var postalCode by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Div({ classes("acc-auth-card") }) {
        Div({ classes("acc-auth-brand") }) {
            Div({ classes("acc-auth-logo") }) { Text("🐙") }
            Div({ classes("acc-auth-title") }) { Text("Complete your profile") }
            Div({ classes("acc-auth-sub") }) {
                Text("Please provide your phone and delivery address to continue")
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Full Name *") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "Your full name")
                value(fullName)
                onInput { fullName = it.value }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Phone Number *") }
            Input(type = InputType.Tel) {
                classes("acc-input")
                attr("placeholder", "10-digit mobile number")
                value(phone)
                onInput { phone = it.value.filter { c -> c.isDigit() }.take(10) }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Address Line 1 *") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "Street address, building, apartment")
                value(addressLine1)
                onInput { addressLine1 = it.value }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Address Line 2 (Optional)") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "Landmark, floor, etc.")
                value(addressLine2)
                onInput { addressLine2 = it.value }
            }
        }

        Div({ style { display(DisplayStyle.Flex); gap(12.px); flexWrap(FlexWrap.Wrap) } }) {
            Div({ classes("acc-field"); style { flex(1); minWidth(120.px) } }) {
                Label(attrs = { classes("acc-label") }) { Text("City *") }
                Input(type = InputType.Text) {
                    classes("acc-input")
                    attr("placeholder", "e.g. Bangalore")
                    value(city)
                    onInput { city = it.value }
                }
            }
            Div({ classes("acc-field"); style { flex(1); minWidth(100.px) } }) {
                Label(attrs = { classes("acc-label") }) { Text("Postal Code *") }
                Input(type = InputType.Text) {
                    classes("acc-input")
                    attr("placeholder", "e.g. 560066")
                    value(postalCode)
                    onInput { postalCode = it.value.filter { c -> c.isDigit() }.take(6) }
                }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("State (Optional)") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "e.g. Karnataka")
                value(state)
                onInput { state = it.value }
            }
        }

        if (error != null) P({ classes("acc-msg-err") }) { Text(error!!) }

        val isFormValid = fullName.isNotBlank() && phone.length == 10 &&
            addressLine1.isNotBlank() && city.isNotBlank() && postalCode.length >= 5

        Button({
            classes("acc-btn", "acc-btn-primary")
            style { marginTop(4.px) }
            onClick {
                if (!isFormValid || isLoading) return@onClick
                isLoading = true
                error = null
                scope.launch {
                    try {
                        FirebaseService.updateUserProfileRequiredFields(
                            name = fullName,
                            phone = phone,
                            addressLine1 = addressLine1,
                            addressLine2 = addressLine2,
                            city = city,
                            state = state,
                            postalCode = postalCode
                        )
                        onComplete()
                    } catch (e: Throwable) {
                        error = e.message ?: "Failed to save profile"
                    } finally {
                        isLoading = false
                    }
                }
            }
            if (!isFormValid || isLoading) attr("disabled", "true")
        }) { Text(if (isLoading) "Saving..." else "Complete Profile") }
    }
}

@Composable
fun AccountDashboard(user: UserProfile, onSignOut: () -> Unit) {
    var activeSection by remember { mutableStateOf("dashboard") }
    var isNutritionist by remember { mutableStateOf(false) }

    LaunchedEffect(user.userId) {
        val uid = user.userId ?: return@LaunchedEffect
        try {
            val db = FirebaseService.getFirestore()
            val docRef = com.example.borshi.web.firebase.FirebaseFirestore.doc(db, "users", uid)
            val snap = com.example.borshi.web.firebase.FirebaseFirestore.getDoc(docRef).await()
            if (snap.exists()) {
                val data = snap.data()
                val flag = data.isNutritionist
                isNutritionist = flag == true || flag == "True" || flag == "true"
            }
        } catch (e: Throwable) {
            console.warn("Could not read isNutritionist flag:", e.message)
        }
    }
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(20.px)
            backgroundColor(Color(BG_LIGHT))
            minHeight(80.vh)
        }
    }) {
        Div({ classes("acc-header-card") }) {
            Div({ classes("acc-header-top") }) {
                Span({ classes("acc-header-title") }) { Text("My Account") }
                Button({ classes("acc-signout"); onClick { onSignOut() } }) { Text("Sign Out") }
            }
            Div({ classes("acc-profile") }) {
                Div({ classes("acc-avatar") }) { Text("👤") }
                Div({}) {
                    val displayName = user.name
                    val phone = user.phoneNumber
                    val email = user.email
                    if (!displayName.isNullOrBlank()) Div({ classes("acc-name") }) { Text(displayName) }
                    if (!phone.isNullOrBlank()) Div({ classes("acc-sub") }) { Text(phone) }
                    if (!email.isNullOrBlank()) Div({ classes("acc-sub") }) { Text(email) }
                    if (displayName.isNullOrBlank() && phone.isNullOrBlank() && email.isNullOrBlank()) {
                        Div({ classes("acc-name") }) { Text("Guest User") }
                    }
                }
            }
        }
        if (activeSection == "dashboard") {
            AccountSectionsGrid(isNutritionist) { activeSection = it }
        } else {
            Div({ classes("acc-back"); onClick { activeSection = "dashboard" } }) { Text("← Back to Dashboard") }
            when (activeSection) {
                "wallet" -> WalletSection(user)
                "referral" -> ReferralScreen()
                "appointments" -> AppointmentsSection { activeSection = "dashboard" }
                "schedule" -> SubscriptionScheduleSection(user)
                "orders" -> OrderHistoryScreen { activeSection = "dashboard" }
                "addresses" -> ManageAddressesScreen { activeSection = "dashboard" }
                "payments" -> PaymentsScreen { activeSection = "dashboard" }
                "accountDetails" -> AccountDetailsScreen(user) { activeSection = "dashboard" }
                "support" -> SupportSection { activeSection = "dashboard" }
                "nutritionist" -> { Div { Text("Nutritionist Dashboard") } }
                else -> Div { Text("Section: $activeSection") }
            }
        }
    }
}

@Composable
private fun AccountSectionsGrid(
    isNutritionist: Boolean = false,
    onSectionClick: (String) -> Unit
) {
    val sections = buildList {
        // Only show Nutritionist Dashboard if user has isNutritionist == "True" in Firestore
        if (isNutritionist) {
            add(AccountSectionItem("Nutritionist Dashboard", "🩺", "nutritionist"))
        }
        add(AccountSectionItem("My Wallet", "💰", "wallet"))
        add(AccountSectionItem("Refer & Earn", "🎁", "referral"))
        add(AccountSectionItem("Appointments", "🩺", "appointments"))
        add(AccountSectionItem("My Schedule", "📅", "schedule"))
        add(AccountSectionItem("My Orders", "📦", "orders"))
        add(AccountSectionItem("Saved Addresses", "📍", "addresses"))
        add(AccountSectionItem("Support", "🎧", "support"))
    }

    Div({ classes("acc-grid") }) {
        sections.forEach { section ->
            AccountSectionCard(section, onSectionClick)
        }
    }
}

@Composable
private fun AccountSectionCard(section: AccountSectionItem, onClick: (String) -> Unit) {
    var isHovered by remember { mutableStateOf(false) }
    Div({
        style {
            padding(28.px)
            backgroundColor(Color.white)
            borderRadius(16.px)
            textAlign("center")
            cursor("pointer")
            property("transition", "all 0.25s cubic-bezier(0.22,1,0.36,1)")
            property("box-shadow", if (isHovered) "0 8px 32px rgba(13,43,46,0.12)" else "0 2px 10px rgba(13,43,46,0.06)")
            property("transform", if (isHovered) "translateY(-4px)" else "translateY(0)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(12.px)
            border(1.5.px, LineStyle.Solid, if (isHovered) Color(TEAL_ACC) else Color(BORDER_COLOR))
        }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
        onClick { onClick(section.id) }
    }) {
        Div({
            style {
                fontSize(36.px)
                property("transition", "transform 0.25s ease")
                property("transform", if (isHovered) "scale(1.15)" else "scale(1)")
            }
        }) { Text(section.icon) }
        H3({
            style {
                fontSize(15.px)
                margin(0.px)
                fontWeight("700")
                fontFamily("DM Sans, sans-serif")
                color(if (isHovered) Color(TEAL_ACC) else Color(TEXT_DARK))
                property("transition", "color 0.2s ease")
            }
        }) { Text(section.title) }
    }
}

// Classes moved to AuthModels.kt

// Functions moved to AccountSections.kt

@Composable
fun LoginSection(authViewModel: AuthViewModel, onSwitchToSignUp: () -> Unit) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var phoneDigits by remember { mutableStateOf("") }
    var otp by remember { mutableStateOf("") }
    val signInState = authViewModel.signInState
    val otpState = authViewModel.otpState

    val sanitizedPhone = remember(phoneDigits) { phoneDigits.filter { it.isDigit() }.take(10) }
    val fullPhoneNumber = if (sanitizedPhone.isNotBlank()) "+91$sanitizedPhone" else ""
    val canSendOtp = sanitizedPhone.length == 10 && otpState !is OtpState.Sending && otpState !is OtpState.Verifying
    val readableOtpHint = (otpState as? OtpState.OtpSent)?.phoneNumber
    val signInError = (signInState as? SignInState.Error)?.message
    val otpError = (otpState as? OtpState.Error)?.message

    Div({ classes("acc-auth-card") }) {
        // Brand header
        Div({ classes("acc-auth-brand") }) {
            Div({ classes("acc-auth-logo") }) { Text("🐙") }
            Div({ classes("acc-auth-title") }) { Text("Welcome back") }
            Div({ classes("acc-auth-sub") }) { Text("Sign in to manage your orders and plans") }
        }

        // Google & Anonymous quick sign-in
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px); marginBottom(20.px) } }) {
            Button({
                classes("acc-btn")
                style {
                    backgroundColor(Color.white)
                    color(Color("#333"))
                    border(1.5.px, LineStyle.Solid, Color(BORDER_COLOR))
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(8.px)
                }
                onClick { authViewModel.signInWithGoogle() }
                if (signInState is SignInState.Loading) attr("disabled", "true")
            }) { Text("Continue with Google") }
            Button({
                classes("acc-btn")
                style {
                    backgroundColor(Color("#F5F5F5"))
                    color(Color("#666"))
                    border(1.5.px, LineStyle.Solid, Color(BORDER_COLOR))
                }
                onClick { authViewModel.signInAnonymously() }
                if (signInState is SignInState.Loading) attr("disabled", "true")
            }) { Text("Continue as Guest") }
        }
        signInError?.let { P({ classes("acc-msg-err") }) { Text(it) } }

        // Divider
        Div({ classes("acc-divider") }) {
            Div({ classes("acc-divider-line") }) {}
            Span({ classes("acc-divider-text") }) { Text("or sign in with") }
            Div({ classes("acc-divider-line") }) {}
        }

        // Phone Number Input
        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Phone Number") }
            Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
                Input(type = InputType.Text) {
                    classes("acc-prefix")
                    value("+91")
                    attr("disabled", "true")
                }
                Input(type = InputType.Tel) {
                    classes("acc-input")
                    style { flex(1) }
                    attr("placeholder", "10-digit mobile number")
                    attr("maxlength", "10")
                    attr("inputmode", "numeric")
                    value(sanitizedPhone)
                    onInput { phoneDigits = it.value.filter { ch: Char -> ch.isDigit() }.take(10) }
                }
            }
        }

        Button({
            classes("acc-btn", "acc-btn-primary")
            style { marginTop(4.px) }
            onClick { if (fullPhoneNumber.isNotBlank()) authViewModel.sendOtp(fullPhoneNumber) }
            if (!canSendOtp) attr("disabled", "true")
        }) {
            when (otpState) {
                OtpState.Sending    -> Text("Sending OTP...")
                is OtpState.OtpSent -> Text("Resend OTP")
                else                -> Text("Send OTP")
            }
        }

        readableOtpHint?.let {
            P({ classes("acc-msg-ok") }) { Text("OTP sent to ${maskPhoneNumber(it)}") }
        }
        otpError?.let { P({ classes("acc-msg-err") }) { Text(it) } }

        if (otpState is OtpState.OtpSent || otpState is OtpState.Verifying) {
            Div({ classes("acc-field"); style { marginTop(16.px) } }) {
                Label(attrs = { classes("acc-label") }) { Text("Enter OTP") }
                Input(type = InputType.Text) {
                    classes("acc-input")
                    attr("placeholder", "6-digit OTP")
                    attr("maxlength", "6")
                    attr("inputmode", "numeric")
                    value(otp)
                    onInput { otp = it.value.filter { ch: Char -> ch.isDigit() }.take(6) }
                }
            }
            Button({
                classes("acc-btn", "acc-btn-primary")
                style { marginTop(4.px) }
                onClick { authViewModel.verifyOtp(otp) }
                if (otp.length < 6 || otpState is OtpState.Sending || otpState is OtpState.Verifying) attr("disabled", "true")
            }) {
                when (otpState) {
                    OtpState.Verifying -> Text("Verifying...")
                    OtpState.Sending   -> Text("Sending...")
                    else               -> Text("Verify OTP")
                }
            }
        }

        FirebaseTestAccountsHint()
        RecaptchaAnchor()

        // Divider
        Div({ classes("acc-divider") }) {
            Div({ classes("acc-divider-line") }) {}
            Span({ classes("acc-divider-text") }) { Text("or use email") }
            Div({ classes("acc-divider-line") }) {}
        }

        // Email Input
        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Email Address") }
            Input(type = InputType.Email) {
                classes("acc-input")
                attr("placeholder", "your@email.com")
                value(email)
                onInput { email = it.value }
            }
        }

        // Password Input
        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Password") }
            Input(type = InputType.Password) {
                classes("acc-input")
                attr("placeholder", "Enter your password")
                value(password)
                onInput { password = it.value }
            }
        }

        signInError?.let {
            P({
                style {
                    color(Color("#C62828"))
                    textAlign("center")
                    marginTop(8.px)
                }
            }) {
                Text(it)
            }
        }

        Button({
            classes("acc-btn", "acc-btn-primary")
            style { marginTop(4.px) }
            onClick { authViewModel.signIn(email, password) }
            if (signInState is SignInState.Loading) attr("disabled", "true")
        }) {
            if (signInState is SignInState.Loading) Text("Signing in...") else Text("Continue with Email")
        }

        signInError?.let { P({ classes("acc-msg-err") }) { Text(it) } }

        Div({ classes("acc-switch") }) {
            A(href = "#", attrs = {
                style { color(Color(TEAL_ACC)); textDecoration("none"); fontSize(12.px); fontFamily("DM Sans, sans-serif") }
                onClick {
                    it.preventDefault()
                    val targetEmail = email.trim()
                    if (targetEmail.isEmpty()) {
                        kotlinx.browser.window.alert("Enter your email to reset password")
                    } else {
                        try {
                            FirebaseService.sendPasswordReset(targetEmail)
                            kotlinx.browser.window.alert("Password reset link sent if the email exists")
                        } catch (e: Throwable) {
                            kotlinx.browser.window.alert(e.message ?: "Failed to send reset link")
                        }
                    }
                }
            }) { Text("Forgot password?") }
        }

        Div({ classes("acc-switch") }) {
            Text("New to Borshi? ")
            Button({
                classes("acc-switch-link")
                onClick { onSwitchToSignUp() }
            }) { Text("Create account") }
        }
    }
}

@Composable
fun SignUpSection(authViewModel: AuthViewModel, onSwitchToLogin: () -> Unit) {
    var fullName by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var addressLine1 by remember { mutableStateOf("") }
    var addressLine2 by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var state by remember { mutableStateOf("") }
    var postalCode by remember { mutableStateOf("") }
    val signUpState = authViewModel.signUpState

    Div({ classes("acc-auth-card") }) {
        Div({ classes("acc-auth-brand") }) {
            Div({ classes("acc-auth-logo") }) { Text("🐙") }
            Div({ classes("acc-auth-title") }) { Text("Create account") }
            Div({ classes("acc-auth-sub") }) { Text("Join Borshi for fresh seafood delivered to your door") }
        }

        // Google sign-up (creates account on first use)
        Button({
            classes("acc-btn")
            style {
                width(100.percent)
                backgroundColor(Color.white)
                color(Color("#333"))
                border(1.5.px, LineStyle.Solid, Color(BORDER_COLOR))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(8.px)
                marginBottom(16.px)
            }
            onClick { authViewModel.signInWithGoogle() }
            if (signUpState is SignUpState.Loading) attr("disabled", "true")
        }) { Text("Continue with Google") }
        if (signUpState is SignUpState.Error) { P({ classes("acc-msg-err") }) { Text(signUpState.message) } }

        Div({ classes("acc-divider"); style { marginBottom(16.px) } }) {
            Div({ classes("acc-divider-line") }) {}
            Span({ classes("acc-divider-text") }) { Text("or sign up with email") }
            Div({ classes("acc-divider-line") }) {}
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Full Name *") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "Your full name")
                value(fullName)
                onInput { fullName = it.value }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Phone Number *") }
            Input(type = InputType.Tel) {
                classes("acc-input")
                attr("placeholder", "10-digit mobile number")
                value(phone)
                onInput { phone = it.value.filter { c -> c.isDigit() }.take(10) }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Email Address *") }
            Input(type = InputType.Email) {
                classes("acc-input")
                attr("placeholder", "your@email.com")
                value(email)
                onInput { email = it.value }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Address Line 1 *") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "Street address, building, apartment")
                value(addressLine1)
                onInput { addressLine1 = it.value }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Address Line 2 (Optional)") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "Landmark, floor, etc.")
                value(addressLine2)
                onInput { addressLine2 = it.value }
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            Div({ classes("acc-field"); style { flex(1); minWidth(120.px) } }) {
                Label(attrs = { classes("acc-label") }) { Text("City *") }
                Input(type = InputType.Text) {
                    classes("acc-input")
                    attr("placeholder", "e.g. Bangalore")
                    value(city)
                    onInput { city = it.value }
                }
            }
            Div({ classes("acc-field"); style { flex(1); minWidth(100.px) } }) {
                Label(attrs = { classes("acc-label") }) { Text("Postal Code *") }
                Input(type = InputType.Text) {
                    classes("acc-input")
                    attr("placeholder", "e.g. 560066")
                    value(postalCode)
                    onInput { postalCode = it.value.filter { c -> c.isDigit() }.take(6) }
                }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("State (Optional)") }
            Input(type = InputType.Text) {
                classes("acc-input")
                attr("placeholder", "e.g. Karnataka")
                value(state)
                onInput { state = it.value }
            }
        }

        Div({ classes("acc-field") }) {
            Label(attrs = { classes("acc-label") }) { Text("Password *") }
            Input(type = InputType.Password) {
                classes("acc-input")
                attr("placeholder", "Create a password (8+ characters)")
                value(password)
                onInput { password = it.value }
            }
        }

        if (signUpState is SignUpState.Error) {
            P({ classes("acc-msg-err") }) { Text(signUpState.message) }
        }

        val isFormValid = fullName.isNotBlank() && phone.length == 10 && email.contains("@")
            && addressLine1.isNotBlank() && city.isNotBlank() && postalCode.length >= 5
            && password.length >= 8

        Button({
            classes("acc-btn", "acc-btn-primary")
            style { marginTop(4.px) }
            onClick {
                authViewModel.signUp(
                    email = email,
                    password = password,
                    name = fullName,
                    phone = phone,
                    addressLine1 = addressLine1,
                    addressLine2 = addressLine2,
                    city = city,
                    state = state,
                    postalCode = postalCode
                )
            }
            if (signUpState is SignUpState.Loading || !isFormValid) attr("disabled", "true")
        }) {
            if (signUpState is SignUpState.Loading) Text("Creating Account...") else Text("Create Account")
        }

        Div({ classes("acc-switch") }) {
            Text("Already have an account? ")
            Button({
                classes("acc-switch-link")
                onClick { onSwitchToLogin() }
            }) { Text("Sign In") }
        }
    }
}

@Composable
private fun FirebaseTestAccountsHint() {
    if (!SHOW_TEST_ACCOUNTS) return
    if (!FirebaseTestAccounts.hasEntries()) return

    Div({
        style {
            marginTop(20.px)
            padding(16.px)
            borderRadius(12.px)
            backgroundColor(Color("#FFF4F7"))
            property("border", "1px solid #FFD1DC")
        }
    }) {
        P({
            style {
                fontWeight("600")
                marginBottom(8.px)
                color(Color("#AD1457"))
            }
        }) {
            Text("Firebase test numbers")
        }

        Ul({
            style {
                paddingLeft(18.px)
                margin(0.px)
            }
        }) {
            FirebaseTestAccounts.phoneOtpPairs.forEach { pair ->
                Li({
                    style {
                        fontSize(13.px)
                        color(Color("#4A4A4A"))
                    }
                }) {
                    Text("${pair.first} · OTP ${pair.second}")
                }
            }
        }

        P({
            style {
                marginTop(8.px)
                fontSize(12.px)
                color(Color("#6D0D2F"))
            }
        }) {
            Text("Use the same entries under Authentication > Sign-in method > Phone > Testing in the Firebase console.")
        }
    }
}

@Composable
private fun RecaptchaAnchor(anchorId: String = "recaptcha-container") {
    Div({
        id(anchorId)
        style {
            marginTop(16.px)
            minHeight(78.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
        }
    })
}

private fun maskPhoneNumber(phoneNumber: String): String {
    if (phoneNumber.length <= 4) return phoneNumber
    val visible = phoneNumber.takeLast(4)
    val maskLength = phoneNumber.length - 4
    return "●".repeat(maskLength) + visible
}

// ─────────────────────────────────────────────────────────────────────────────
// Appointments Section
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun AppointmentsSection(onBack: () -> Unit) {
    var appointments by remember { mutableStateOf<List<com.example.borshi.web.firebase.FirebaseService.Appointment>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        try {
            appointments = com.example.borshi.web.firebase.FirebaseService.getAppointments()
        } catch (e: Throwable) {
            console.warn("Failed to load appointments:", e)
        } finally {
            isLoading = false
        }
    }

    Div({
        style {
            backgroundColor(Color.white)
            borderRadius(16.px)
            padding(32.px)
            property("box-shadow", "0 2px 12px rgba(13,43,46,0.06)")
        }
    }) {
        Div({
            style {
                fontSize(22.px); fontWeight("700")
                color(Color("#0D2B2E")); marginBottom(24.px)
            }
        }) { Text("🩺 Upcoming Appointments") }

        if (isLoading) {
            Div({
                style {
                    textAlign("center"); padding(40.px, 0.px)
                    color(Color("#7A9598")); fontSize(14.px)
                }
            }) { Text("Loading appointments...") }
        } else if (appointments.isEmpty()) {
            Div({
                style {
                    textAlign("center"); padding(48.px, 0.px); color(Color("#7A9598"))
                }
            }) {
                Div({ style { fontSize(40.px); marginBottom(12.px) } }) { Text("📅") }
                P({ style { fontSize(15.px); fontWeight("500") } }) {
                    Text("No appointments scheduled yet.")
                }
                P({ style { fontSize(13.px); color(Color("#9E9E9E")); marginTop(8.px) } }) {
                    Text("Book a nutritionist consultation from any plan page to get started.")
                }
            }
        } else {
            appointments.forEach { appt ->
                val isUpcoming = appt.status == "upcoming"
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.FlexStart)
                        gap(16.px)
                        padding(20.px)
                        borderRadius(12.px)
                        backgroundColor(if (isUpcoming) Color("#F0FAFA") else Color("#F5F5F5"))
                        border(1.px, LineStyle.Solid, if (isUpcoming) Color("#26A69A") else Color("#E0E0E0"))
                        marginBottom(12.px)
                    }
                }) {
                    Div({
                        style {
                            width(48.px); height(48.px); borderRadius(12.px)
                            backgroundColor(if (isUpcoming) Color("#26A69A") else Color("#9E9E9E"))
                            color(Color.white)
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            fontSize(20.px); flexShrink(0)
                        }
                    }) { Text(if (isUpcoming) "🩺" else "✓") }

                    Div({ style { flex(1) } }) {
                        Div({
                            style {
                                fontSize(16.px); fontWeight("700"); color(Color("#0D2B2E"))
                                marginBottom(4.px)
                            }
                        }) { Text(appt.planName) }

                        Div({
                            style {
                                fontSize(14.px); color(Color("#5A6C6D")); marginBottom(4.px)
                            }
                        }) { Text("👨‍⚕️ ${appt.nutritionistName.ifBlank { "Borshi Nutritionist" }}") }

                        Div({
                            style {
                                fontSize(13.px); fontWeight("600")
                                color(if (isUpcoming) Color("#1A7A72") else Color("#9E9E9E"))
                            }
                        }) { Text("📅 ${appt.slotLabel}") }

                        Div({
                            style {
                                marginTop(8.px)
                                display(DisplayStyle.Flex); gap(8.px)
                            }
                        }) {
                            Span({
                                style {
                                    fontSize(11.px); fontWeight("700")
                                    padding(4.px, 12.px); borderRadius(999.px)
                                    backgroundColor(
                                        when (appt.status) {
                                            "upcoming" -> Color("#E0F2F1")
                                            "completed" -> Color("#E8F5E9")
                                            "cancelled" -> Color("#FFEBEE")
                                            else -> Color("#F5F5F5")
                                        }
                                    )
                                    color(
                                        when (appt.status) {
                                            "upcoming" -> Color("#1A7A72")
                                            "completed" -> Color("#2E7D32")
                                            "cancelled" -> Color("#C62828")
                                            else -> Color("#9E9E9E")
                                        }
                                    )
                                }
                            }) { Text(appt.status.uppercase()) }
                        }
                    }
                }
            }
        }
    }
}

// UserProfile and AuthState moved to AuthModels.kt

@Composable
fun SupportSection(onBack: () -> Unit) {
    Div({
        style {
            backgroundColor(Color.white)
            padding(24.px)
            borderRadius(12.px)
            property("box-shadow", "0 2px 6px rgba(0,0,0,0.08)")
        }
    }) {
        H3({ style { marginBottom(12.px) } }) { Text("Support") }
        P({ style { marginBottom(16.px); color(Color("#555")) } }) {
            Text("Need help? Email us at support@borshi.com or call +91-00000-00000.")
        }
    }
}
